import React, { useEffect, useRef, useState } from 'react'
import {
  DEFAULT_CONTAINER_ID,
  useSequencerStore,
} from '@/stores/sequencerStore'

/**
 * Compact save / rename affordance for the current container.
 *
 * Two modes driven by whether the current container is the default
 * scratch pad:
 *
 *   - Default container: labels a prominent "Save Project" button.
 *     Clicking opens an inline input pre-filled with a suggested
 *     name; commit promotes the default container to a proper UUID-
 *     keyed one via `saveDefaultProject`. Without this step the
 *     sync push loop explicitly skips the default-project row, so
 *     work on it lives only in localStorage and is lost on any
 *     state reset.
 *
 *   - Any other container: labels the current name. Clicking swaps
 *     in an inline input for rename; commit calls `renameContainer`.
 *     Sync push picks up the new name on its next tick.
 *
 * Intended for the Sequencer's workspace header (rightExtras slot).
 * Stays narrow so the single-row header stays single-row.
 */
export const ProjectSaveRename: React.FC = () => {
  const currentContainerId = useSequencerStore(s => s.currentContainerId)
  const containers = useSequencerStore(s => s.containers)
  const renameContainer = useSequencerStore(s => s.renameContainer)
  const saveDefaultProject = useSequencerStore(s => s.saveDefaultProject)
  const container = containers[currentContainerId]

  const isDefault = currentContainerId === DEFAULT_CONTAINER_ID
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState('')
  const inputRef = useRef<HTMLInputElement | null>(null)

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus()
      inputRef.current.select()
    }
  }, [editing])

  if (!container) return null

  const startEdit = () => {
    setDraft(isDefault ? '' : (container.name ?? ''))
    setEditing(true)
  }

  const commit = () => {
    const value = draft.trim()
    if (isDefault) {
      saveDefaultProject(value)
    } else if (value && value !== container.name) {
      renameContainer(currentContainerId, value)
    }
    setEditing(false)
  }

  const cancel = () => {
    setDraft('')
    setEditing(false)
  }

  if (editing) {
    return (
      <input
        ref={inputRef}
        type="text"
        value={draft}
        onChange={e => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={e => {
          if (e.key === 'Enter') commit()
          if (e.key === 'Escape') cancel()
        }}
        placeholder={isDefault ? 'Name your project…' : container.name}
        className="text-[10px] font-mono uppercase tracking-wider rounded border border-accent bg-surface-sunken text-content-primary px-2 py-1 w-44 focus:outline-none focus:ring-1 focus:ring-[color:var(--accent-halo)]"
        aria-label={isDefault ? 'Save project as' : 'Rename project'}
      />
    )
  }

  if (isDefault) {
    return (
      <button
        type="button"
        onClick={startEdit}
        title="Save the current scratch-pad project under a name. Cloud sync picks it up on the next push."
        className="text-[10px] font-mono uppercase tracking-wider rounded px-2 py-1 border border-accent bg-accent/20 text-accent hover:bg-accent hover:text-[color:var(--accent-ink)] transition-colors"
      >
        💾 Save Project
      </button>
    )
  }

  return (
    <button
      type="button"
      onClick={startEdit}
      title="Rename this project"
      className="text-[10px] font-mono uppercase tracking-wider rounded px-2 py-1 border border-brd-subtle text-content-secondary hover:border-accent hover:text-accent transition-colors"
    >
      ✎ Rename
    </button>
  )
}
